import { useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { Export } from '@revenuemarket/ui';
import { Menu, MenuProps, Typography } from 'antd';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

import { CategoryTreeItem } from '~apis/categories';
import { GroupTreeItem } from '~apis/custom-label';
import GuideButton from '~components/ui/Button/GuideButton';
import { FlattenTreeResult } from '~components/ui/HorizontalTreeTable/Table';
import { LABEL_TYPES_USAGE_GUIDE_LINK } from '~constants/guide';
import { usePageWidth } from '~hooks/usePageWidth';
import { css } from '~libs/emotion';

import CategoryLabelManage from './CategoryLabelManage';
import GroupLabelManage from './GroupLabelManage';
import GroupLabelManagementPanel from './GroupLabelManagementPanel';
import LabelManagementPanel from './LabelManagementPanel';

type MenuItem = Required<MenuProps>['items'][number];
const items: MenuItem[] = [
  {
    key: 'categoryLabel',
    label: (
      <Typography.Text
        style={{
          ...typographyStyle['14_400'],
        }}
      >
        계정
      </Typography.Text>
    ),
  },
  {
    key: 'groupLabel',
    label: (
      <Typography.Text
        style={{
          ...typographyStyle['14_400'],
        }}
      >
        그룹
      </Typography.Text>
    ),
  },
  {
    type: 'divider',
  },
  {
    key: 'businessEntityLabel',
    label: (
      <a target="_blank" href="/clobe/sales-purchase" rel="noopener noreferrer">
        <Flex
          justify="space-between"
          align="center"
          style={{
            lineHeight: 32,
            height: 32,
          }}
        >
          <Typography.Text
            style={{
              ...typographyStyle['14_400'],
            }}
          >
            거래처
          </Typography.Text>
          <Export />
        </Flex>
      </a>
    ),
  },
];

interface Props {
  showSidebar?: boolean;
  defaultMenu?: string;
}

const LabelManagement: React.FC<Props> = ({ defaultMenu, showSidebar = true }) => {
  const searchParams = useSearchParams();
  const currentTab = searchParams.get('tab');
  const [menu, setMenu] = useState(defaultMenu ?? currentTab ?? 'categoryLabel');

  const [activeItem, setActiveItem] =
    useState<FlattenTreeResult<CategoryTreeItem | GroupTreeItem>>();

  const activeItemRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pageWidth = usePageWidth(0, [activeItemRef]);

  return (
    <Flex
      justify="space-between"
      className={css`
        *::-webkit-scrollbar-track {
          margin-top: 30px !important;
        }
      `}
    >
      <Flex
        direction="column"
        style={{
          width: '100%',
          maxWidth: pageWidth,
        }}
      >
        {showSidebar ? (
          <Flex
            style={{
              padding: '24px 24px 0px 24px',
            }}
          >
            <Typography
              style={{
                ...typographyStyle['16_600'],
              }}
            >
              {'분류 계정 관리'}
            </Typography>
            <GuideButton link={LABEL_TYPES_USAGE_GUIDE_LINK} />
          </Flex>
        ) : (
          <div />
        )}
        <Flex
          style={{
            padding: 16,
            width: 'inherit',
          }}
        >
          <Flex
            gap={24}
            style={{
              width: '100%',
            }}
          >
            {showSidebar ? (
              <Menu
                activeKey={menu}
                style={{
                  backgroundColor: 'transparent',
                  width: 140,
                  border: 'none',
                }}
                onSelect={({ key }) => {
                  if (key === 'businessEntityLabel') {
                    return;
                  }
                  router.replace({
                    query: {
                      tab: key,
                    },
                  }); // URL 업데이트

                  setActiveItem(undefined);
                  setMenu(key);
                }}
                items={items}
                selectedKeys={[menu]}
                className={css`
                  & .ant-menu-item-selected {
                    background-color: ${Colors.grayOpacity04};
                    & > span > span {
                      font-weight: 500 !important;
                    }
                  }
                  & .ant-menu-item {
                    width: 140px;
                    margin-top: 0px !important;
                    margin-bottom: 0px !important;
                    padding: 6px 8px !important;
                    margin-left: 6px !important;
                    display: flex;
                    align-items: center;
                    & > span {
                      width: inherit;
                    }
                  }
                `}
              />
            ) : null}
            {menu === 'categoryLabel' ? (
              <CategoryLabelManage
                activeItem={activeItem as FlattenTreeResult<CategoryTreeItem>}
                setActiveItem={setActiveItem}
              />
            ) : (
              <GroupLabelManage
                activeItem={activeItem as FlattenTreeResult<GroupTreeItem>}
                setActiveItem={setActiveItem}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      {activeItem?.value && (
        <Flex
          ref={activeItemRef}
          style={{
            width: 420,
            padding: '8px 8px 8px 0px',
          }}
        >
          {menu === 'categoryLabel' ? (
            <LabelManagementPanel
              setActiveItem={setActiveItem}
              activeKey={activeItem.key}
              viewType={activeItem.status === 'new' ? 'new' : 'info'}
            />
          ) : (
            <GroupLabelManagementPanel
              setActiveItem={setActiveItem}
              activeKey={activeItem.key}
              viewType={activeItem.status === 'new' ? 'new' : 'info'}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default LabelManagement;
